/**
 * Workspace persistence — load/save a `UserWorkspace` over HTTP + msgpack.
 *
 * The web app holds the workspace as the single source of truth; this client
 * mirrors it to qv-data's `/set/*` endpoints so edits survive a reload.
 * Rendering-agnostic, like the rest of transport — it deals in the contract
 * `UserWorkspace` and `Theme` types only.
 *
 * Two granularities, and the choice matters:
 *
 * - {@link WorkspaceStore.save} writes the whole tree. Use it for structural
 *   edits — adding a pane, changing a symbol, editing screener columns.
 * - {@link WorkspaceStore.saveLayoutSizes} and
 *   {@link WorkspaceStore.savePaneHeights} write one field each. Use them for
 *   drags. A divider is dragged continuously, and rewriting the entire
 *   workspace per frame would both waste the round trip and let a resize
 *   clobber an unrelated edit made since the tree was last read.
 *
 * **Round-trip note.** The server stores the workspace relationally, so what
 * comes back is normalised, not byte-identical: an empty optional container is
 * indistinguishable from an absent one (`annotations: []` reads back absent;
 * `sync: {}` likewise), and a contract `number` written as `1` returns as `1`
 * having passed through a float column. Nothing a consumer acts on changes.
 */

import { encode, decode } from "@msgpack/msgpack";
import type { Theme, UserWorkspace } from "@qufolio/model";

/** Encode a workspace to msgpack bytes. */
export function encodeWorkspace(ws: UserWorkspace): Uint8Array {
  return encode(ws);
}

/** Decode msgpack bytes back into a workspace (unvalidated — caller may validate). */
export function decodeWorkspace(bytes: Uint8Array | ArrayBuffer): UserWorkspace {
  return decode(bytes instanceof ArrayBuffer ? new Uint8Array(bytes) : bytes) as UserWorkspace;
}

/** Encode a theme to msgpack bytes. */
export function encodeTheme(theme: Theme): Uint8Array {
  return encode(theme);
}

/** Decode msgpack bytes back into a theme list (the shape `/set/themes` returns). */
export function decodeThemes(bytes: Uint8Array | ArrayBuffer): Theme[] {
  const value = decode(bytes instanceof ArrayBuffer ? new Uint8Array(bytes) : bytes);
  return Array.isArray(value) ? (value as Theme[]) : [];
}

/** Grid track sizes for one layout. An omitted side is left as-is server-side. */
export interface LayoutSizes {
  /** CSS Grid row sizes, e.g. `["3fr", "1fr"]`. */
  rowSizes?: string[];
  /** CSS Grid column sizes — where chart-vs-screener width lives. */
  columnSizes?: string[];
  /**
   * The divider inside the dock (`Layout.dock.sizes`), one per panel showing.
   * The server ignores it when it does not fit the panels it holds.
   */
  dockSizes?: string[];
}

/** One pane's height, as sent to `/set/upsert/paneheights`. */
export interface PaneHeight {
  /** The pane's contract ULID. */
  id: string;
  /** CSS-style height, e.g. `"3fr"` or `"300px"`. */
  height: string;
}

export interface WorkspaceStoreOptions {
  /** Base HTTP(S) URL of qv-data, e.g. `http://127.0.0.1:8000`. */
  baseUrl: string;
  /**
   * Firebase ID token, sent as `Authorization: Bearer`.
   *
   * **A getter, not a string**, because an ID token expires in an hour and the
   * SDK refreshes it in the background. A value captured when the store was
   * constructed is the stale one by the time a workspace is saved, and a stale
   * token is a 401 on a write the user believes succeeded — which is the exact
   * failure `persistence.ts` is built around never having.
   *
   * A plain string is still accepted for a caller that genuinely has a fixed
   * one, and `undefined` means send no header at all.
   */
  idToken?: string | (() => string | null | Promise<string | null>);
  /**
   * Extra headers, resolved per request.
   *
   * The host uses this for `X-Act-As`, which says *whose* workspace rather than
   * who is asking — and it has to travel with the token, since a request
   * carrying one without the other reads the wrong account's layout under the
   * right person's name. Kept generic here: this package has no notion of
   * impersonation and should not gain one.
   */
  headers?: () => Record<string, string> | Promise<Record<string, string>>;
  /** Injectable fetch (defaults to the global). Keeps the client testable. */
  fetch?: typeof fetch;
}

/** Client for qv-data's msgpack workspace endpoints (`/set/*`). */
export class WorkspaceStore {
  private readonly base: string;
  private readonly fetchFn: typeof fetch;

  constructor(private readonly options: WorkspaceStoreOptions) {
    this.base = options.baseUrl.replace(/\/+$/u, "");
    const f = options.fetch ?? (globalThis as { fetch?: typeof fetch }).fetch;
    if (!f) throw new Error("No global fetch available; pass options.fetch.");
    // Bound, not just referenced. A browser's `window.fetch` throws
    // "Illegal invocation" when called with the wrong receiver, and detaching it
    // onto a field does exactly that — while Node's plain function does not care.
    // Unbound therefore passes every test run under Node and fails in every
    // browser, which is the worst way for this to be wrong.
    this.fetchFn = f.bind(globalThis);
  }

  private async headers(extra?: Record<string, string>): Promise<Record<string, string>> {
    const h: Record<string, string> = { ...extra };
    const source = this.options.idToken;
    const token = typeof source === "function" ? await source() : source;
    if (token) h["Authorization"] = `Bearer ${token}`;
    Object.assign(h, (await this.options.headers?.()) ?? {});
    return h;
  }

  /** Load a user's saved workspace, or null if none is stored yet. */
  async load(userId: string | number): Promise<UserWorkspace | null> {
    const res = await this.fetchFn(`${this.base}/set/${encodeURIComponent(String(userId))}`, {
      method: "GET",
      headers: await this.headers(),
    });
    if (res.status === 404) return null;
    if (!res.ok) throw new Error(`load workspace failed: HTTP ${res.status}`);
    const buf = await res.arrayBuffer();
    if (buf.byteLength === 0) return null;
    return decodeWorkspace(buf);
  }

  /** Upsert a user's whole workspace. */
  async save(userId: string | number, workspace: UserWorkspace): Promise<void> {
    await this.post("userworkspace", userId, encodeWorkspace(workspace), "save workspace");
  }

  /**
   * Write one layout's grid track sizes, leaving the rest of the tree alone.
   *
   * Pass only the axis that moved: omitting `columnSizes` leaves the stored
   * column widths untouched rather than clearing them, so a row drag can never
   * take the chart/screener split with it.
   *
   * Throws if the layout is unknown to the server (HTTP 404) — that means the
   * client is holding a layout the server never saw, which is worth surfacing
   * rather than swallowing as a no-op.
   */
  async saveLayoutSizes(
    userId: string | number,
    layoutId: string,
    sizes: LayoutSizes,
  ): Promise<void> {
    const body: Record<string, unknown> = { layoutId };
    if (sizes.rowSizes) body["rowSizes"] = sizes.rowSizes;
    if (sizes.columnSizes) body["columnSizes"] = sizes.columnSizes;
    if (sizes.dockSizes) body["dockSizes"] = sizes.dockSizes;
    await this.post("layoutsizes", userId, encode(body), "save layout sizes");
  }

  /**
   * Write pane heights in one request.
   *
   * Takes a list because one divider drag resizes the two panes either side of
   * it; sending them as separate requests would let the pair land out of step.
   * A no-op when `panes` is empty. Throws on HTTP 404 (unknown pane id).
   */
  async savePaneHeights(userId: string | number, panes: PaneHeight[]): Promise<void> {
    if (panes.length === 0) return;
    await this.post("paneheights", userId, encode({ panes }), "save pane heights");
  }

  /** Load a user's saved themes. Empty array when none are stored. */
  async loadThemes(userId: string | number): Promise<Theme[]> {
    const res = await this.fetchFn(
      `${this.base}/set/themes/${encodeURIComponent(String(userId))}`,
      { method: "GET", headers: await this.headers() },
    );
    if (res.status === 404) return [];
    if (!res.ok) throw new Error(`load themes failed: HTTP ${res.status}`);
    const buf = await res.arrayBuffer();
    if (buf.byteLength === 0) return [];
    return decodeThemes(buf);
  }

  /** Upsert one theme, keyed by its contract id. */
  async saveTheme(userId: string | number, theme: Theme): Promise<void> {
    await this.post("theme", userId, encodeTheme(theme), "save theme");
  }

  /** POST msgpack to `/set/upsert/{path}?user_id=…`, throwing on any non-2xx. */
  private async post(
    path: string,
    userId: string | number,
    body: Uint8Array,
    what: string,
  ): Promise<void> {
    const res = await this.fetchFn(
      `${this.base}/set/upsert/${path}?user_id=${encodeURIComponent(String(userId))}`,
      {
        method: "POST",
        headers: await this.headers({ "Content-Type": "application/msgpack" }),
        body: body as BodyInit,
      },
    );
    if (!res.ok) throw new Error(`${what} failed: HTTP ${res.status}`);
  }
}
